import React from 'react';
import BrandDropdown from 'shared/components/ui/BrandDropdown';
import { Plus, X } from 'lucide-react';
import TagInput from './TagInput';

/**
 * A node's typed output fields — what it returns, and what its edges branch on.
 *
 *   boolean                 → true / false
 *   number  {min, max}      → a score in a declared range
 *   enum    {values: [...]} → one of the named members
 *
 * The instrument must return exactly these; the executor refuses anything
 * else (output_type_mismatch). Field names: lowercase, digits, underscore.
 */

const input = 'h-7 rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-base)] px-2 text-[12px] text-[color:var(--text-primary)] outline-none focus:border-[color:var(--border-strong)]';

export function fieldEntries(output) {
  return Object.entries(output || {});
}

const TYPE_OPTIONS = [{ value: 'boolean', label: 'true / false' }, { value: 'number', label: 'number' }, { value: 'enum', label: 'one of…' }];

export default function OutputFieldsEditor({ value, onChange, readOnly = false, single = false }) {
  const entries = fieldEntries(value);

  const write = (next) => onChange(Object.fromEntries(next));
  const setName = (i, name) => write(entries.map((e, j) => (j === i ? [name, e[1]] : e)));
  const setSpec = (i, spec) => write(entries.map((e, j) => (j === i ? [e[0], spec] : e)));
  const setType = (i, type) => {
    const spec = type === 'number' ? { type, min: 0, max: 1 } : type === 'enum' ? { type, values: ['yes', 'no'] } : { type };
    setSpec(i, spec);
  };
  const add = () => {
    const taken = new Set(entries.map(([n]) => n));
    let name = 'value'; let k = 2;
    while (taken.has(name)) { name = `value_${k}`; k += 1; }
    write([...entries, [name, { type: 'boolean' }]]);
  };

  return (
    <div className="space-y-1.5">
      {entries.map(([name, spec], i) => (
        <div key={i} className="rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] p-2">
          <div className="flex items-center gap-1.5">
            <input value={name} disabled={readOnly} aria-label={`Output field ${i + 1} name`} onChange={(e) => setName(i, e.target.value.replace(/[^a-z0-9_]/g, '_').toLowerCase())}
              className={`${input} w-[40%] font-mono`} />
            <div className="w-[30%]"><BrandDropdown compact ariaLabel={`Output field ${i + 1} type`} value={spec.type} disabled={readOnly} onChange={(t) => setType(i, t)} options={TYPE_OPTIONS} /></div>
            <div className="flex-1" />
            {!readOnly && !single && (
              <button type="button" onClick={() => write(entries.filter((_, j) => j !== i))} aria-label={`Remove output field ${i + 1}`}
                className="rounded p-1 text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--feedback-error)]"><X size={12} /></button>
            )}
          </div>
          {spec.type === 'number' && (
            <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-[color:var(--text-muted)]">
              from
              <input type="number" step="any" value={spec.min ?? ''} disabled={readOnly} aria-label={`Output field ${i + 1} min`}
                onChange={(e) => setSpec(i, { ...spec, min: Number(e.target.value) })} className={`${input} w-20`} />
              to
              <input type="number" step="any" value={spec.max ?? ''} disabled={readOnly} aria-label={`Output field ${i + 1} max`}
                onChange={(e) => setSpec(i, { ...spec, max: Number(e.target.value) })} className={`${input} w-20`} />
            </div>
          )}
          {spec.type === 'enum' && (
            <div className="mt-1.5 flex items-start gap-1.5 text-[11px] text-[color:var(--text-muted)]">
              <span className="pt-2">options</span>
              <div className="min-w-0 flex-1">
                <TagInput value={spec.values || []} readOnly={readOnly} ariaLabel={`Output field ${i + 1} members`} placeholder="Type an option and press Enter"
                  onChange={(values) => setSpec(i, { ...spec, values })} />
              </div>
            </div>
          )}
        </div>
      ))}
      {!readOnly && !single && (
        <button type="button" onClick={add} className="inline-flex items-center gap-1 rounded-md border border-dashed border-[color:var(--border-default)] px-2 py-1 text-[11px] text-[color:var(--text-secondary)] hover:bg-surface-hover">
          <Plus size={11} /> Add field
        </button>
      )}
    </div>
  );
}
